import { Center, Select, Stack } from '@mantine/core'
import { Suspense, useEffect, useRef } from 'react'
import { z } from 'zod'
import { IconChartPie } from '@tabler/icons-react'
import RingProgressCard from '../../../../stories/RingProgressCard/RingProgressCard'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { Fmt, useFmtColor } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import { useResolvedContext } from './useResolvedContext'
import {
  ColorSchemaFields,
  ComponentStatisticFields,
  ContextFields,
  ContextSchemaFields,
  NEUTRAL_COLOR,
  NeutralColorsToggle,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'

const RING_SIZE_PX = 220

const Schema = z.object({
  ...ContextSchemaFields,
  ...ColorSchemaFields,
  metric_key: z.string().default(''),
  component: z.string().default('percent'),
  statistic: z.string().default('value'),
})

type Settings = z.infer<typeof Schema>

function pickMetric(metrics: Metric[], settings: Settings): Metric | undefined {
  if (settings.metric_key) {
    return metrics.find((m) => m.key === settings.metric_key)
  }
  return metrics.find((m) => m.is_kpi)
}

function FormattedRing({
  metricKey,
  title,
  percentValue,
  actualValue,
  displayComponent,
  statistic,
  neutral,
}: {
  metricKey: string
  title: string
  percentValue: number
  actualValue: number | null
  displayComponent: string
  statistic: string
  neutral: boolean
}) {
  const thresholdColor = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic,
    value: percentValue,
  })
  const color = neutral ? NEUTRAL_COLOR : thresholdColor
  const valueLabel =
    displayComponent === 'actual' ? (
      <Fmt
        metric={metricKey}
        component="actual"
        statistic={statistic}
        value={actualValue}
      />
    ) : (
      <Fmt
        metric={metricKey}
        component="percent"
        statistic={statistic}
        value={percentValue}
      />
    )
  return (
    <RingProgressCard
      title={title}
      value={percentValue * 100}
      color={color}
      size={RING_SIZE_PX}
      valueLabel={valueLabel}
    />
  )
}

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metric = pickMetric(allMetrics, settings)
  const ctx = useResolvedContext(settings)
  const metricKeys = metric ? [metric.key] : []

  const { data: percentPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: metricKeys,
    components: metricKeys.map(() => 'percent'),
    statistics: metricKeys.map(() => settings.statistic),
    competition_id: ctx.competition_id,
  })

  const { data: labelPoints } = useMetricsData({
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: metricKeys,
    components: metricKeys.map(() => settings.component),
    statistics: metricKeys.map(() => settings.statistic),
    competition_id: ctx.competition_id,
  })

  if (!metric) return <WidgetEmpty message="No metric" />
  const percentPoint = percentPoints.find((p) => p.metric === metric.metric_id)
  if (!percentPoint) return <WidgetEmpty message="No data" />
  const actualValue =
    labelPoints.find((p) => p.metric === metric.metric_id)?.value ?? null

  return (
    <Center w="100%" h="100%">
      <FormattedRing
        metricKey={metric.key}
        title={metric.short_name || metric.name}
        percentValue={percentPoint.value}
        actualValue={actualValue}
        displayComponent={settings.component}
        statistic={settings.statistic}
        neutral={settings.use_neutral_colors}
      />
    </Center>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSection boxed={false}>
      <WidgetSuspense>
        <Inner settings={settings} />
      </WidgetSuspense>
    </WidgetSection>
  )
}

function KpiSingleSelectInner({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const { data: metrics } = useMetrics()
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (hydratedRef.current) return
    if (value) {
      hydratedRef.current = true
      return
    }
    if (metrics.length === 0) return
    const firstKpi = metrics.find((m) => m.is_kpi)
    hydratedRef.current = true
    if (firstKpi) onChange(firstKpi.key)
  }, [metrics, value, onChange])

  const data = metrics
    .filter((m) => m.is_kpi)
    .map((m) => ({ value: m.key, label: m.name || m.key }))

  return (
    <Select
      label="KPI"
      placeholder="Pick a KPI"
      data={data}
      value={value || null}
      onChange={(v) => onChange(v || '')}
      searchable
      clearable
      nothingFoundMessage="No KPI metrics"
    />
  )
}

function KpiSingleSelect(props: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Suspense
      fallback={
        <Select label="KPI" data={[]} disabled placeholder="Loading..." />
      }
    >
      <KpiSingleSelectInner {...props} />
    </Suspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <KpiSingleSelect
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <ComponentStatisticFields
        component={value.component}
        statistic={value.statistic}
        compatibility={KpiRingGridWidget.compatibility}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <ContextFields
        subject_type={value.subject_type}
        subject_id={value.subject_id}
        period_type={value.period_type}
        period={value.period}
        use_max_period={value.use_max_period}
        onChange={(p) => patch(p as Partial<Settings>)}
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const KpiRingGridWidget: DashboardWidget<Settings> = {
  type: 'kpi_ring_grid',
  label: 'KPI Ring',
  description: 'Circular progress ring for a single KPI',
  icon: IconChartPie,
  minW: 1,
  minH: 1,
  maxW: 1,
  maxH: 1,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  compatibility: {
    percent: ['value', 'value_ytd'],
    actual: ['value', 'value_ytd'],
  },
  Component,
  OptionsForm,
}
